import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, ListChecks, Loader2, Plus, RefreshCw, Sparkles, Target, Users, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { useNexusRefresh } from '../../context/NexusSyncContext';
import PrepareMeetingDialog from './PrepareMeetingDialog';
import ObjectivePicker from './ObjectivePicker';
import DayAgenda from './DayAgenda';
import TodayMeetings from './TodayMeetings';
import MailCard from './MailCard';
import TacticalProposals from './TacticalProposals';
import ProjectCards, { DeadlineChip } from './ProjectCards';
import useLivePolling from './useLivePolling';
import ProgressReviewCard from '../ProgressReview/ProgressReviewCard';
import KickoffHints from './KickoffHints';

// La page se met à jour seule (agenda, tâches, réunions à préparer) : toutes les minutes si elle est visible, et
// dès qu'on y revient ; les cartes de projets, plus lourdes, toutes les cinq minutes.
const LIVE_MS = 60000;
const CARDS_MS = 5 * 60000;

// Accueil : la page de la journée de chacun. En haut, le mot de Muse ; puis les réunions du jour avec leurs points,
// l'emploi du temps (deux jours, navigable) et « Ma journée » (tâches et objectifs du jour) ; enfin une fiche par
// projet actif où l'on a un rôle, avec l'avancement et les nouveautés tenus par Muse.

const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// Jour ouvré précédent (le week-end est sauté).
const previousWorkday = (iso) => {
  const date = new Date(`${iso}T12:00:00`);
  do date.setDate(date.getDate() - 1); while (date.getDay() === 0 || date.getDay() === 6);
  return isoDay(date);
};

const readDismissed = (day) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(`deepflow.home.dismissed.${day}`) || '[]'));
  } catch (err) {
    return new Set();
  }
};

const writeDismissed = (day, set) => {
  try {
    localStorage.setItem(`deepflow.home.dismissed.${day}`, JSON.stringify([...set]));
  } catch (err) {
    // stockage indisponible : l'écart ne vaut que pour cette visite
  }
};

const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');

const shortDate = (iso) => new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
  .format(new Date(`${iso.slice(0, 10)}T12:00:00`));

// ------------------------------------------------------------------ mot de Muse
const MuseCard = ({ brief, onRequest, requesting }) => {
  const pending = brief.status === 'queued' || brief.status === 'running';
  return (
    <section className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-white px-4 py-3.5 shadow-sm sm:px-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm shadow-indigo-200">
          <Sparkles size={15} />
        </span>
        <div className="min-w-0 flex-1">
          {brief.status === 'ready' && brief.message ? (
            <>
              <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-800">{brief.message}</p>
              <p className="mt-1 text-xs text-slate-400">Préparé par Nexus{brief.generated_at ? ` à ${timeOf(brief.generated_at)}` : ''}</p>
            </>
          ) : pending ? (
            <p className="flex items-center gap-2 py-1 text-sm text-slate-600">
              <Loader2 size={14} className="animate-spin text-indigo-500" /> Nexus prépare ta journée… (quelques minutes)
            </p>
          ) : (
            <>
              <p className="py-1 text-sm text-slate-600">
                {brief.status === 'failed' ? "Nexus n'a pas pu préparer ta journée ce matin." : "Nexus n'a pas encore préparé ta journée."}
              </p>
              {brief.status === 'failed' && brief.error && <p className="text-xs text-slate-400">{brief.error}</p>}
            </>
          )}
        </div>
        {!pending && (
          <button type="button" onClick={onRequest} disabled={requesting}
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100/60 disabled:opacity-50 sm:px-2.5"
            title="Demander à Nexus de (re)préparer la journée" aria-label="Préparer la journée">
            {requesting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            <span className="hidden sm:inline">{brief.status === 'ready' ? 'Refaire' : 'Préparer maintenant'}</span>
          </button>
        )}
      </div>
    </section>
  );
};

// ------------------------------------------------------------------ ma journée
const Row = ({ icon: Icon, iconClass = 'text-slate-400', title, meta, children, onOpen }) => (
  <div className="group flex items-start gap-3 rounded-xl px-2 py-2 hover:bg-slate-50">
    {Icon && <Icon size={16} className={`mt-0.5 shrink-0 ${iconClass}`} />}
    <div className="min-w-0 flex-1">
      <button type="button" onClick={onOpen} className="block max-w-full text-left text-sm font-medium leading-snug text-slate-800 hover:text-indigo-700">
        {title}
      </button>
      {meta && <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">{meta}</div>}
    </div>
    <div className="flex shrink-0 items-center gap-1">{children}</div>
  </div>
);

const objectiveMeta = (objective, today) => {
  const parts = [<span key="p">{objective.project_name}</span>];
  if (objective.is_milestone && objective.milestone_date) {
    parts.push(<DeadlineChip key="m" today={today} deadline={{ date: objective.milestone_date, label: 'Jalon', late: objective.milestone_date < today }} />);
  } else if (objective.sprint_end) {
    parts.push(<span key="s">sprint jusqu'au {shortDate(objective.sprint_end)}</span>);
  }
  if (objective.status === 'blocked') parts.push(<span key="b" className="font-medium text-rose-600">bloqué</span>);
  return parts;
};

const SubTitle = ({ children, action }) => (
  <div className="mb-0.5 mt-1 flex items-center justify-between gap-2 px-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{children}</p>
    {action}
  </div>
);

// Ajout rapide d'une tâche pour aujourd'hui (à moi ; projet facultatif, parmi mes projets actifs).
const QuickTask = ({ projects, onAdd }) => {
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await onAdd(name.trim(), projectId ? Number(projectId) : null);
      setName('');
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-1 flex items-center gap-1.5 px-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ajouter une tâche pour aujourd'hui…"
        className="min-w-0 flex-1 rounded-lg border border-transparent bg-slate-50 px-2.5 py-1.5 text-sm outline-none placeholder:text-slate-400 focus:border-indigo-200 focus:bg-white focus:ring-2 focus:ring-indigo-50" />
      {name.trim() && projects.length > 0 && (
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} title="Projet"
          className="max-w-[8rem] rounded-lg border border-slate-200 bg-white px-1.5 py-1.5 text-xs text-slate-600">
          <option value="">Sans projet</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      <button type="submit" disabled={!name.trim() || saving} title="Ajouter"
        className="rounded-lg bg-slate-900 p-1.5 text-white hover:bg-slate-700 disabled:opacity-30">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
      </button>
    </form>
  );
};

const DayCard = ({ data, projects, onChooseObjective, onUnchoose, onPlanTask, onTaskDone, onTaskLater, onAddTask, onPick, onDismiss, dismissed, busy, openProject }) => {
  const { objectives, tasks } = data.day_plan;
  const proposals = data.proposals.filter((p) => !dismissed.has(`${p.kind}:${p.id}`));
  const fromMuse = proposals.some((p) => p.by === 'muse');
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Target size={16} className="text-slate-400" /> Ma journée</h2>
      </div>

      <SubTitle>Tâches du jour{tasks.length > 0 ? ` · ${tasks.length}` : ''}</SubTitle>
      <div className="space-y-0.5">
        {tasks.map((task) => (
          <div key={`t${task.id}`} className="group flex items-start gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50">
            <button type="button" title="Terminée" onClick={() => onTaskDone(task)} disabled={busy.has(`t${task.id}`)}
              className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500">
              <Check size={11} />
            </button>
            <div className="min-w-0 flex-1">
              <button type="button" onClick={() => openProject(task.project_id)} className="block max-w-full text-left text-sm leading-snug text-slate-800 hover:text-indigo-700">{task.name}</button>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                {task.project_name && <span>{task.project_name}</span>}
                {task.priority === 'urgent' && <span className="font-medium text-rose-600">urgent</span>}
                {task.due_date && <DeadlineChip today={data.today} deadline={{ date: task.due_date, label: 'Échéance' }} />}
              </div>
            </div>
            <button type="button" onClick={() => onTaskLater(task)} className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-slate-400 opacity-0 hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100">
              Demain
            </button>
          </div>
        ))}
        {!tasks.length && <p className="px-2 py-1 text-sm text-slate-400">Aucune tâche prévue aujourd'hui.</p>}
      </div>
      <QuickTask projects={projects} onAdd={onAddTask} />

      <SubTitle action={<button type="button" onClick={onPick} className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline"><Plus size={12} /> Choisir</button>}>
        Objectifs du jour
      </SubTitle>
      {!objectives.length ? (
        <p className="px-2 pb-1 text-sm text-slate-400">Aucun objectif choisi : pioche dans les propositions ou choisis-en.</p>
      ) : (
        <div className="space-y-0.5">
          {objectives.map((objective) => (
            <Row key={`o${objective.id}`} icon={Target} iconClass="text-indigo-500" title={objective.name}
              onOpen={() => openProject(objective.project_id)}
              meta={[...objectiveMeta(objective, data.today),
                ...(objective.also?.length ? [<span key="a" className="flex items-center gap-1"><Users size={11} /> avec {objective.also.join(', ')}</span>] : [])]}>
              <button type="button" title="Retirer de ma journée" onClick={() => onUnchoose(objective)} disabled={busy.has(`o${objective.id}`)}
                className="rounded-md p-1 text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-500 group-hover:opacity-100">
                <X size={14} />
              </button>
            </Row>
          ))}
        </div>
      )}

      {proposals.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="mb-1 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {fromMuse ? <><Sparkles size={12} className="text-indigo-400" /> Proposé par Nexus</> : 'Suggestions'}
          </p>
          {proposals.map((item) => (
            <Row key={`${item.kind}${item.id}`} icon={item.kind === 'objective' ? Target : ListChecks}
              title={item.name} onOpen={() => openProject(item.project_id)}
              meta={[<span key="p">{item.project_name}</span>,
                ...(item.reason ? [<span key="r" className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{item.reason}</span>] : [])]}>
              <button type="button" disabled={busy.has(`${item.kind}${item.id}`)}
                onClick={() => (item.kind === 'objective' ? onChooseObjective(item) : onPlanTask(item))}
                className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
                {busy.has(`${item.kind}${item.id}`) ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Aujourd'hui
              </button>
              <button type="button" title="Pas aujourd'hui" onClick={() => onDismiss(item)} className="rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500">
                <X size={14} />
              </button>
            </Row>
          ))}
        </div>
      )}
    </section>
  );
};

const Skeleton = () => (
  <div className="mx-auto w-full max-w-[1920px] animate-pulse space-y-6 px-4 py-6 lg:px-8">
    <div className="flex justify-between"><div className="h-10 w-64 rounded-lg bg-slate-200" /><div className="h-14 w-96 rounded-2xl bg-slate-100" /></div>
    <div className="h-20 rounded-2xl bg-slate-100" />
    <div className="grid gap-6 xl:grid-cols-12">
      <div className="h-[600px] rounded-2xl bg-slate-100 xl:col-span-8" />
      <div className="h-[600px] rounded-2xl bg-slate-100 xl:col-span-4" />
    </div>
  </div>
);

// ------------------------------------------------------------------ page
const HomePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cards, setCards] = useState(null);
  const [cardsError, setCardsError] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [moving, setMoving] = useState(false);
  const [openPoints, setOpenPoints] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [prepare, setPrepare] = useState(null);
  const [picking, setPicking] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const loadingRef = useRef(false);
  const scheduleStart = useRef(null); // jour affiché en premier dans l'emploi du temps (null : aujourd'hui)
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const overview = await apiService.get('/home/overview');
      setData(overview);
      if (!scheduleStart.current || scheduleStart.current === overview.today) setSchedule(overview.schedule);
      setOpenPoints(Object.fromEntries(overview.prepare.map((m) => [m.event_id, m.open_points])));
      setDismissed(readDismissed(overview.today));
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  const loadCards = useCallback(async () => {
    try {
      setCards(await apiService.get('/home/projects'));
      setCardsError(null);
    } catch (err) {
      setCardsError(err.message);
    }
  }, []);

  useEffect(() => { load(); loadCards(); }, [load, loadCards]);
  useLivePolling(load, LIVE_MS);
  useLivePolling(loadCards, CARDS_MS, { minGapMs: CARDS_MS });
  useNexusRefresh(['tasks', 'objectives'], load);

  // Pendant que Muse prépare la journée, on regarde de temps en temps si c'est prêt.
  const briefStatus = data?.brief.status;
  useEffect(() => {
    if (briefStatus !== 'queued' && briefStatus !== 'running') return undefined;
    const timer = setInterval(async () => {
      try {
        const brief = await apiService.get('/home/brief');
        if (brief.status !== briefStatus) {
          load();
          loadCards();
        }
      } catch (err) {
        // on réessaiera au prochain passage
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [briefStatus, load, loadCards]);

  const withBusy = async (key, action) => {
    setBusy((prev) => new Set(prev).add(key));
    try {
      await action();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const openProject = (projectId) => projectId && navigate(`/project/${projectId}`);
  const chooseObjective = (objective) => withBusy(`objective${objective.id}`, () => apiService.post(`/home/day-plan/objectives/${objective.id}`, {}));
  const unchoose = (objective) => withBusy(`o${objective.id}`, () => apiService.delete(`/home/day-plan/objectives/${objective.id}`));
  const planTask = (task) => withBusy(`task${task.id}`, () => apiService.patch(`/tasks/${task.id}`, { planning_status: 'today' }));
  const taskDone = (task) => withBusy(`t${task.id}`, () => apiService.patch(`/tasks/${task.id}`, { is_completed: true }));
  const taskLater = (task) => withBusy(`t${task.id}`, () => apiService.patch(`/tasks/${task.id}`, { planning_status: 'tomorrow' }));
  const addTask = (name, projectId) => withBusy('newtask', () => apiService.post('/tasks', {
    name, owner_id: data.employee.id, planning_status: 'today', ...(projectId ? { project_id: projectId } : {}),
  }));

  // Emploi du temps : un jour ouvré de plus ou de moins (0 : revenir à aujourd'hui).
  const moveSchedule = async (direction) => {
    const current = schedule?.start || data.today;
    const start = direction === 0 ? data.today
      : direction > 0 ? (schedule?.days?.[1]?.date || current) : previousWorkday(current);
    if (start === current && direction !== 0) return;
    setMoving(true);
    try {
      setSchedule(await apiService.get(`/home/schedule?start=${start}`));
      scheduleStart.current = start;
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setMoving(false);
    }
  };
  const dismiss = (item) => {
    const next = new Set(dismissed).add(`${item.kind}:${item.id}`);
    setDismissed(next);
    writeDismissed(data.today, next);
  };
  const requestBrief = async () => {
    setRequesting(true);
    try {
      await apiService.post('/home/brief', {});
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRequesting(false);
    }
  };

  // Réunion de la semaine ouverte depuis le calendrier : celles du jour gardent leurs points déjà chargés.
  const openEvent = (event, day) => {
    const today = data.prepare.find((m) => m.event_id === event.id);
    setPrepare(today || {
      event_id: event.id, series_id: event.series_id, title: event.title, time: event.time, end_time: event.end_time,
      external: event.external, link: event.link, project_id: event.project_id, project_name: event.project_name,
      client_id: event.client_id, client_name: event.client_name,
      day_label: day.date === data.today ? null : day.label,
    });
  };

  const myProjects = useMemo(() => (cards?.cards || []).map((c) => ({ id: c.id, name: c.name })), [cards]);

  if (!data) {
    return error ? <div className="mx-auto max-w-2xl py-16 text-center text-sm text-slate-500">{error}</div> : <Skeleton />;
  }

  const hour = new Date().getHours();
  const chosenIds = new Set(data.day_plan.objectives.map((o) => o.id));

  return (
    <div className="mx-auto w-full max-w-[1920px] space-y-5 px-3 py-4 sm:space-y-6 sm:px-4 sm:py-6 lg:px-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{hour >= 18 ? 'Bonsoir' : 'Bonjour'} {data.employee.first_name}</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
            <Clock size={13} /> {data.today_label.charAt(0).toUpperCase() + data.today_label.slice(1)}
            {updatedAt && (
              <button type="button" onClick={load} title="Actualiser (la page se met à jour seule chaque minute)"
                className="group ml-1 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs text-slate-400 hover:bg-white hover:text-slate-600">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                à jour à {updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                <RefreshCw size={11} className="opacity-0 transition group-hover:opacity-100" />
              </button>
            )}
          </p>
        </div>
      </header>

      <MuseCard brief={data.brief} onRequest={requestBrief} requesting={requesting} />
      <ProgressReviewCard today={data.today} navigate={navigate} />
      <KickoffHints hints={data.kickoffs} today={data.today} navigate={navigate} />
      {error && <p className="text-sm text-rose-600">{error}</p>}

      <div className="grid grid-cols-1 items-start gap-5 sm:gap-6 lg:grid-cols-2 xl:grid-cols-12">
        <div className="order-2 min-w-0 lg:order-none xl:col-span-5">
          <TodayMeetings meetings={data.prepare} nextDay={data.prepare_next} connected={data.agenda.connected}
            onPrepare={setPrepare} onLinked={load}
            onCountChange={(eventId, count) => setOpenPoints((prev) => ({ ...prev, [eventId]: count }))} navigate={navigate} />
        </div>
        <div className="order-3 min-w-0 lg:order-none xl:col-span-4">
          <DayAgenda schedule={schedule || data.schedule} today={data.today} openPoints={openPoints} onOpenEvent={openEvent}
            navigate={navigate} onMove={moveSchedule} moving={moving} />
        </div>
        <div className="order-1 min-w-0 lg:order-none lg:col-span-2 xl:col-span-3">
          <DayCard data={data} projects={myProjects} busy={busy} dismissed={dismissed} openProject={openProject}
            onChooseObjective={chooseObjective} onUnchoose={unchoose} onPlanTask={planTask} onAddTask={addTask}
            onTaskDone={taskDone} onTaskLater={taskLater} onPick={() => setPicking(true)} onDismiss={dismiss} />
        </div>
      </div>

      <TacticalProposals onTaskCreated={load} />
      <MailCard today={data.today} projects={myProjects} onTaskCreated={load} />

      <ProjectCards data={cards} error={cardsError} today={data.today} openProject={openProject} navigate={navigate} />

      {prepare && <PrepareMeetingDialog meeting={prepare} onClose={() => { setPrepare(null); load(); }} />}
      {picking && (
        <ObjectivePicker employeeId={data.employee.id} chosenIds={chosenIds} onClose={() => setPicking(false)}
          onChoose={(objective) => chooseObjective(objective)} />
      )}
    </div>
  );
};

export default HomePage;
